import { useRef, useCallback } from 'react';
import { useWebSocketEvents } from './use-websocket';

export function useTypingIndicator(conversationId: string | null) {
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingStateRef = useRef<boolean>(false);
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastEmitTimeRef = useRef<number>(0);
  const { emit, isConnected } = useWebSocketEvents();

  // Throttled typing indicator - emits WebSocket event with debouncing
  const setTyping = useCallback(
    (isTyping: boolean) => {
      if (!conversationId || !isConnected) return;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // Only update if state changed
      if (isTyping !== lastTypingStateRef.current) {
        lastTypingStateRef.current = isTyping;
        
        // Debounce: send typing_start immediately, typing_stop after 1 second of no typing
        const now = Date.now();
        if (isTyping) {
          // Send typing_start immediately (with throttle to prevent spam)
          if (now - lastEmitTimeRef.current > 300) {
            lastEmitTimeRef.current = now;
            emit('typing_start', { conversation_id: conversationId });
          }
        } else {
          // Send typing_stop
          if (now - lastEmitTimeRef.current > 300) {
            lastEmitTimeRef.current = now;
            emit('typing_stop', { conversation_id: conversationId });
          }
        }
      }

      // Auto-clear typing after 3 seconds of inactivity
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          if (lastTypingStateRef.current) {
            lastTypingStateRef.current = false;
            emit('typing_stop', { conversation_id: conversationId });
          }
        }, 3000);
      }
    },
    [conversationId, isConnected, emit]
  );

  // Cleanup function
  const stopTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = null;
    }
    if (lastTypingStateRef.current && conversationId && isConnected) {
      lastTypingStateRef.current = false;
      emit('typing_stop', { conversation_id: conversationId });
    }
  }, [conversationId, isConnected, emit]);

  return {
    setTyping,
    stopTyping,
  };
}
